import { localize } from '../i18n';
import SingleLineText from './ui/SingleLineText';
import Button from './ui/FitButton';
import Dialog from './StableDialog';
import FeatureUpdateDialog from './FeatureUpdateDialog';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, DialogContent, Typography } from '@mui/material';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import PermissionNotice from './PermissionNotice';
import usePermissionMedications from '../hooks/usePermissionMedications';
import { readAlertRuntimeInfo, readNativePlatform } from '../utils/alertMethodSupport';
import WebviewMessenger from '../utils/WebviewMessenger';
import { isMedicationAlertTarget } from '../utils/medicationAlertTarget';
import { FONT_FAMILY } from '../design/tokens';

const DISMISSED_KEY = 'pill_reminder_full_screen_permission_prompt_dismissed';
const LEGACY_SNOOZE_KEY = 'pill_reminder_full_screen_permission_prompt_snoozed_until';
const MIN_FULL_SCREEN_PROMPT_APP_VERSION = '1.0.5';

function shouldPromptForMedication(med) {
    if (!med || !isMedicationAlertTarget(med)) return false;
    return med.alertType === 'FULLSCREEN' || med.alertType === 'VIBRATION';
}

function formatAlertTypeLabel(alertTypes = []) {
    const labels = [];
    if (alertTypes.includes('FULLSCREEN')) labels.push(localize('벨소리 알림'));
    if (alertTypes.includes('VIBRATION')) labels.push(localize('진동 알림'));
    if (labels.length === 0) return localize("알림");
    if (labels.length === 1) return labels[0];
    return localize("{v0}과 {v1}", { v0: labels[0], v1: labels[1] });
}

function isVersionAtLeast(version, minimum) {
    const currentParts = String(version || '').split('.').map((part) => Number.parseInt(part, 10) || 0);
    const minimumParts = String(minimum || '').split('.').map((part) => Number.parseInt(part, 10) || 0);
    const length = Math.max(currentParts.length, minimumParts.length);
    for (let i = 0; i < length; i += 1) {
        const current = currentParts[i] || 0;
        const required = minimumParts[i] || 0;
        if (current > required) return true;
        if (current < required) return false;
    }
    return true;
}

function readStartupDismissed() {
    try {
        return localStorage.getItem(DISMISSED_KEY) === 'true'
            || Date.now() < Number(localStorage.getItem(LEGACY_SNOOZE_KEY) || 0);
    } catch (_) {
        return false;
    }
}

function dismissStartupPrompt() {
    try {
        localStorage.setItem(DISMISSED_KEY, 'true');
        localStorage.removeItem(LEGACY_SNOOZE_KEY);
    } catch (_) {}
}

function useFullScreenIntentPermissionNeed(medications = null) {
    // Permission is mutable: persisted runtime info is not a current OS permission check.
    const [runtimeInfo, setRuntimeInfo] = useState(() => window.__APP_RUNTIME_INFO || null);
    const { medications: activeMeds, error: medicationError, reload } = usePermissionMedications(medications);

    useEffect(() => {
        const handler = (event) => {
            setRuntimeInfo(event?.detail || readAlertRuntimeInfo());
        };
        const refresh = () => WebviewMessenger.send({ type: 'REQUEST_APP_RUNTIME_INFO' });
        const onVisible = () => {
            if (document.visibilityState === 'visible') refresh();
        };
        window.addEventListener('app-runtime-info-updated', handler);
        window.addEventListener('focus', refresh);
        document.addEventListener('visibilitychange', onVisible);
        refresh();
        return () => {
            window.removeEventListener('app-runtime-info-updated', handler);
            window.removeEventListener('focus', refresh);
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, []);

    const nativePlatform = readNativePlatform(runtimeInfo);
    const fullScreenAccess = runtimeInfo?.capabilities?.fullScreenIntentAccess;
    const appVersionSupported = isVersionAtLeast(runtimeInfo?.appVersion, MIN_FULL_SCREEN_PROMPT_APP_VERSION);
    const needsPermission = nativePlatform === 'android'
        && appVersionSupported
        && fullScreenAccess?.supported === true
        && fullScreenAccess?.granted === false
        && Array.isArray(activeMeds)
        && activeMeds.some(shouldPromptForMedication);

    // Android settings can return without a WebView focus/visibility event.
    // Re-query while access is denied; never treat opening settings as a grant.
    const shouldRecheckPermission = nativePlatform === 'android'
        && appVersionSupported
        && fullScreenAccess?.supported === true
        && fullScreenAccess?.granted !== true;
    useEffect(() => {
        if (!shouldRecheckPermission) return undefined;
        const timer = window.setInterval(() => {
            if (document.visibilityState === 'visible') {
                WebviewMessenger.send({ type: 'REQUEST_APP_RUNTIME_INFO' });
            }
        }, 1000);
        return () => window.clearInterval(timer);
    }, [shouldRecheckPermission]);

    const affectedCount = useMemo(
        () => Array.isArray(activeMeds) ? activeMeds.filter(shouldPromptForMedication).length : 0,
        [activeMeds],
    );
    const activeAlertTypes = useMemo(() => {
        if (!Array.isArray(activeMeds)) return [];
        return Array.from(new Set(
            activeMeds
                .filter(shouldPromptForMedication)
                .map((med) => med.alertType)
                .filter(Boolean),
        ));
    }, [activeMeds]);
    const checkFailed = nativePlatform === 'android' && appVersionSupported && fullScreenAccess?.supported === true
        && fullScreenAccess.granted == null && affectedCount > 0;
    const needsUpdate = nativePlatform === 'android' && appVersionSupported && runtimeInfo
        && fullScreenAccess?.supported !== true && affectedCount > 0;
    return { needsPermission, needsUpdate, checkFailed, medicationError, reload, affectedCount, activeAlertTypes, nativePlatform, fullScreenAccess, appVersionSupported };
}

function requestFullScreenIntentSettings(source, affectedCount, alertTypes = []) {
    WebviewMessenger.send({
        type: 'REQUEST_FULL_SCREEN_INTENT_PERMISSION',
        data: {
            source,
            alertType: alertTypes.join(',') || 'active_medications',
            affectedCount,
        },
    });
}

export default function FullScreenIntentPermissionPrompt({ medications = null }) {
    const [startupDismissed, setStartupDismissed] = useState(() => readStartupDismissed());
    const [manualPromptKey, setManualPromptKey] = useState(0);
    const [manualAlertType, setManualAlertType] = useState(null);
    const [displayAlertTypes, setDisplayAlertTypes] = useState([]);
    const { needsPermission, medicationError, affectedCount, activeAlertTypes, nativePlatform, fullScreenAccess, appVersionSupported } = useFullScreenIntentPermissionNeed(medications);
    const canPromptManually = nativePlatform === 'android'
        && appVersionSupported
        && fullScreenAccess?.supported === true
        && fullScreenAccess?.granted === false
        && manualPromptKey > 0;
    const manualCheckFailed = nativePlatform === 'android' && appVersionSupported
        && fullScreenAccess?.supported === true && fullScreenAccess?.granted == null && manualPromptKey > 0;
    const manualUnsupported = nativePlatform === 'android' && appVersionSupported
        && fullScreenAccess?.supported !== true && manualPromptKey > 0;
    const open = canPromptManually || manualCheckFailed || (needsPermission && !medicationError && !startupDismissed);
    const promptAlertTypes = useMemo(
        () => (canPromptManually && manualAlertType ? [manualAlertType] : activeAlertTypes),
        [activeAlertTypes, canPromptManually, manualAlertType],
    );
    const alertTypeLabel = formatAlertTypeLabel(displayAlertTypes.length > 0 ? displayAlertTypes : promptAlertTypes);

    useEffect(() => {
        const promptHandler = (event) => {
            const alertType = event?.detail?.alertType || null;
            setManualAlertType(alertType);
            if (alertType) setDisplayAlertTypes([alertType]);
            setManualPromptKey((key) => key + 1);
            // A previous grant may have been revoked without a WebView lifecycle event.
            WebviewMessenger.send({ type: 'REQUEST_APP_RUNTIME_INFO' });
        };
        const runtimeHandler = (event) => {
            if (event?.detail?.capabilities?.fullScreenIntentAccess?.granted === true) {
                setManualPromptKey(0);
                setManualAlertType(null);
            }
        };
        window.addEventListener('full-screen-intent-permission-needed', promptHandler);
        window.addEventListener('app-runtime-info-updated', runtimeHandler);
        return () => {
            window.removeEventListener('full-screen-intent-permission-needed', promptHandler);
            window.removeEventListener('app-runtime-info-updated', runtimeHandler);
        };
    }, []);

    useEffect(() => {
        if (open && promptAlertTypes.length > 0) {
            setDisplayAlertTypes(promptAlertTypes);
        }
    }, [open, promptAlertTypes]);

    const handleLater = useCallback(() => {
        dismissStartupPrompt();
        setManualPromptKey(0);
        setManualAlertType(null);
        setStartupDismissed(true);
    }, []);

    const handleOpenSettings = useCallback(() => {
        requestFullScreenIntentSettings('app_launch_required_alert', affectedCount, promptAlertTypes);
        setManualPromptKey(0);
        setManualAlertType(null);
        dismissStartupPrompt();
        setStartupDismissed(true);
    }, [affectedCount, promptAlertTypes]);

    return (<>
        <FeatureUpdateDialog open={manualUnsupported} onClose={handleLater} platform={nativePlatform} />
        <Dialog
            open={open}
            onClose={handleLater}
            maxWidth="xs"
            fullWidth
            PaperProps={{
                sx: {
                    width: 'calc(100% - 40px)',
                    maxWidth: 340,
                    mx: '20px',
                    borderRadius: '24px',
                    boxShadow: '0 18px 48px rgba(0,0,0,0.22)',
                    overflow: 'hidden',
                },
            }}
        >
            <DialogContent sx={{
                p: '28px 22px 20px',
                fontFamily: FONT_FAMILY,
            }}>
                <Box sx={{
                    width: 54,
                    height: 54,
                    borderRadius: '18px',
                    bgcolor: '#fffbeb',
                    border: '1px solid #fde68a',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 1.4,
                }}>
                    <NotificationsActiveOutlinedIcon sx={{ fontSize: 27, color: '#8a7000' }} />
                </Box>
                <SingleLineText sx={{
                    fontSize: '1.28rem',
                    fontWeight: 900,
                    color: '#1a1a1a',
                    lineHeight: 1.32,
                    mb: 0.8,
                    wordBreak: 'keep-all',
                }}>
                    {manualCheckFailed ? localize('전체화면 알림 권한 확인 실패') : localize('전체화면 알림 권한이 필요해요')}
                </SingleLineText>
                <Typography sx={{
                    fontSize: '1.02rem',
                    fontWeight: 650,
                    color: '#666',
                    lineHeight: 1.55,
                    wordBreak: 'keep-all',
                    mb: 1.5,
                }}>
                    {manualCheckFailed ? localize('권한 상태를 다시 확인해 주세요. 알림 설정은 유지돼요.')
                        : localize("{v0}을 잠금화면에서 바로 끄려면 이 권한이 필요해요.", { v0: alertTypeLabel })}
                </Typography>
                <SingleLineText
                    sx={{
                        fontSize: '0.9rem',
                        fontWeight: 700,
                        color: '#7A766F',
                        lineHeight: 1.35,
                        whiteSpace: 'nowrap',
                        mb: 1.6,
                    }}
                >{localize("설정에서 ‘전체화면 알림’을 허용해 주세요.")}</SingleLineText>
                <Box sx={{ display: 'flex', gap: '8px' }}>
                    <Button
                        fullWidth
                        onClick={handleLater}
                        sx={{
                            flex: 1,
                            minHeight: 50,
                            border: '1px solid #EDE8DD',
                            bgcolor: '#fff',
                            color: '#7A766F',
                            fontSize: '1rem',
                            fontWeight: 800,
                            borderRadius: '14px',
                            '&:hover': { bgcolor: '#F8F5EE' },
                        }}
                    >{localize("나중에")}</Button>
                    <Button
                        fullWidth
                        onClick={manualCheckFailed ? () => WebviewMessenger.send({ type: 'REQUEST_APP_RUNTIME_INFO' }) : handleOpenSettings}
                        sx={{
                            flex: 1.35,
                            minHeight: 50,
                            bgcolor: '#ffe566',
                            color: '#5a4a00',
                            fontSize: '1.04rem',
                            fontWeight: 900,
                            borderRadius: '14px',
                            boxShadow: '0 4px 12px rgba(230,168,0,0.22)',
                            '&:hover': { bgcolor: '#e8d040', boxShadow: '0 4px 12px rgba(230,168,0,0.22)' },
                        }}
                    >
                        {manualCheckFailed ? localize('다시 확인') : localize('설정 열기')}
                    </Button>
                </Box>
            </DialogContent>
        </Dialog>
        </>
    );
}

export function FullScreenIntentPermissionBanner({ medications = null, source = 'settings_banner' }) {
    const [updateOpen, setUpdateOpen] = useState(false);
    const { needsPermission, needsUpdate, checkFailed, medicationError, reload, nativePlatform, appVersionSupported,
        fullScreenAccess, affectedCount, activeAlertTypes } = useFullScreenIntentPermissionNeed(medications);
    if (nativePlatform === 'android' && appVersionSupported && medicationError) {
        return <PermissionNotice icon={<NotificationsActiveOutlinedIcon />} title={localize("약 정보 확인 필요")} description={localize("알림 대상 약을 불러오지 못했어요.")}
            actionLabel={localize("다시 확인")} onAction={reload} />;
    }
    if (needsUpdate) return <>
        <PermissionNotice icon={<NotificationsActiveOutlinedIcon />} title={localize("전체화면 알림 확인 필요")}
            description={localize("앱을 업데이트해 확인해 주세요.")} actionLabel={localize("업데이트")} onAction={() => setUpdateOpen(true)} />
        <FeatureUpdateDialog open={updateOpen} onClose={() => setUpdateOpen(false)} platform="android" />
    </>;
    if (checkFailed) return <PermissionNotice icon={<NotificationsActiveOutlinedIcon />} title={localize("전체화면 권한 확인 필요")}
        description={localize("권한을 확인하지 못했어요.")} actionLabel={localize("다시 확인")}
        onAction={() => WebviewMessenger.send({ type: 'REQUEST_APP_RUNTIME_INFO' })} />;
    if (!needsPermission) return null;
    return (
        <PermissionNotice
            icon={<NotificationsActiveOutlinedIcon />}
            title={localize("전체화면 알림 권한 필요")}
            description={localize("전체화면 알림을 허용해 주세요.")}
            actionLabel={localize("설정 열기")}
            onAction={() => requestFullScreenIntentSettings(source, affectedCount, activeAlertTypes)}
        />
    );
}
